import React, { useState, useEffect, useRef, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { X, Mic, MicOff } from "lucide-react";

import { debugLog } from "../utils/debugLog";
import webPlayerStorage from "../utils/webPlayerStorage";

// Luna AI Configuration
const LUNA_CONFIG = {
  API_URL: "https://api.lunova.app/luna/voice_command",
  API_KEY: "2f844f2233d6eace060d6842467b5201f6551286fd472823d436fcdcd4dbcd11",
};

const VISUALIZER_OPTIONS = {
  smoothing: 0.6,
  fft: 8,
  minDecibels: -70,
  scale: 0.2,
  glow: 10,
  color1: [203, 36, 128],
  color2: [41, 200, 192],
  color3: [24, 137, 218],
  fillOpacity: 0.6,
  lineWidth: 1,
  blend: "screen",
  shift: 50,
  width: 60,
  amp: 1,
};

const FREQUENCY_SHUFFLE = [1, 3, 0, 4, 2];

// Get Luna voice setting from localStorage
const getLunaVoice = () => {
  try {
    return localStorage.getItem("lunaVoice") || "female";
  } catch {
    return "female";
  }
};

// Luna AI Overlay Component
const LunaAIOverlay = ({
  isOpen,
  onClose,
  onCommand,
  currentScreen = "home",
}) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [responseText, setResponseText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [lunaAnimation, setLunaAnimation] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [lunaVoice, setLunaVoice] = useState(getLunaVoice);
  const [userLocation, setUserLocation] = useState(null);

  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const freqsRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const speechTimeoutRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const sendToLunaAIRef = useRef(null);
  const listenStartTimeRef = useRef(0); // Track when listening started to ignore early echo

  // Load Luna AI animation
  useEffect(() => {
    fetch("/luna_ai.json")
      .then((res) => res.json())
      .then((data) => setLunaAnimation(data))
      .catch((err) => console.error("Failed to load Luna animation:", err));
  }, []);

  // Get user location for weather queries
  useEffect(() => {
    if (isOpen && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          // Location access denied - that's ok, weather won't work
          debugLog("Location access denied");
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }
  }, [isOpen]);

  // Listen for voice setting changes from settings page
  useEffect(() => {
    const handleStorageChange = () => {
      setLunaVoice(getLunaVoice());
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Utility function to create range array
  const range = (i) => Array.from(Array(i).keys());

  // Get frequency for channel and index
  const freq = useCallback((channel, i) => {
    if (!freqsRef.current) return 0;
    const band = 2 * channel + FREQUENCY_SHUFFLE[i] * 6;

    return freqsRef.current[band] || 0;
  }, []);

  // Scale factor for value index
  const scale = useCallback((i, amplitude = 1) => {
    const x = Math.abs(2 - i);
    const s = 3 - x;

    return (s / 3) * amplitude;
  }, []);

  // Draw visualization path
  const drawPath = useCallback(
    (ctx, channel, width, height, amplitude = 1) => {
      const color = VISUALIZER_OPTIONS[`color${channel + 1}`].map(Math.floor);

      ctx.fillStyle = `rgba(${color.join(",")}, ${VISUALIZER_OPTIONS.fillOpacity})`;
      ctx.strokeStyle = ctx.shadowColor = `rgb(${color.join(",")})`;
      ctx.lineWidth = VISUALIZER_OPTIONS.lineWidth;
      ctx.shadowBlur = VISUALIZER_OPTIONS.glow;
      ctx.globalCompositeOperation = VISUALIZER_OPTIONS.blend;

      const m = height / 2;
      const offset = (width - 15 * VISUALIZER_OPTIONS.width) / 2;
      const x = range(15).map(
        (i) =>
          offset +
          channel * VISUALIZER_OPTIONS.shift +
          i * VISUALIZER_OPTIONS.width
      );
      const y = range(5).map((i) =>
        Math.max(0, m - scale(i, amplitude) * freq(channel, i))
      );
      const h = 2 * m;

      ctx.beginPath();
      ctx.moveTo(0, m);
      ctx.lineTo(x[0], m + 1);

      ctx.bezierCurveTo(x[1], m + 1, x[2], y[0], x[3], y[0]);
      ctx.bezierCurveTo(x[4], y[0], x[4], y[1], x[5], y[1]);
      ctx.bezierCurveTo(x[6], y[1], x[6], y[2], x[7], y[2]);
      ctx.bezierCurveTo(x[8], y[2], x[8], y[3], x[9], y[3]);
      ctx.bezierCurveTo(x[10], y[3], x[10], y[4], x[11], y[4]);
      ctx.bezierCurveTo(x[12], y[4], x[12], m, x[13], m);

      ctx.lineTo(width, m + 1);
      ctx.lineTo(x[13], m - 1);

      ctx.bezierCurveTo(x[12], m, x[12], h - y[4], x[11], h - y[4]);
      ctx.bezierCurveTo(x[10], h - y[4], x[10], h - y[3], x[9], h - y[3]);
      ctx.bezierCurveTo(x[8], h - y[3], x[8], h - y[2], x[7], h - y[2]);
      ctx.bezierCurveTo(x[6], h - y[2], x[6], h - y[1], x[5], h - y[1]);
      ctx.bezierCurveTo(x[4], h - y[1], x[4], h - y[0], x[3], h - y[0]);
      ctx.bezierCurveTo(x[2], h - y[0], x[1], m, x[0], m);

      ctx.lineTo(0, m);
      ctx.fill();
      ctx.stroke();
    },
    [freq, scale]
  );

  // Visualize audio
  const visualize = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { width } = canvas;
    const { height } = canvas;

    analyserRef.current.smoothingTimeConstant = VISUALIZER_OPTIONS.smoothing;
    analyserRef.current.fftSize = Math.pow(2, VISUALIZER_OPTIONS.fft);
    analyserRef.current.minDecibels = VISUALIZER_OPTIONS.minDecibels;
    analyserRef.current.maxDecibels = 0;
    analyserRef.current.getByteFrequencyData(freqsRef.current);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw three curves (R/G/B)
    drawPath(ctx, 0, width, height, VISUALIZER_OPTIONS.amp);
    drawPath(ctx, 1, width, height, VISUALIZER_OPTIONS.amp);
    drawPath(ctx, 2, width, height, VISUALIZER_OPTIONS.amp);

    animationFrameRef.current = requestAnimationFrame(visualize);
  }, [drawPath]);

  // Initialize audio context and microphone
  const initAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaStreamRef.current = stream;

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      freqsRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);

      const input = audioContextRef.current.createMediaStreamSource(stream);

      input.connect(analyserRef.current);

      setPermissionGranted(true);
      visualize();

      return true;
    } catch (err) {
      console.error("Failed to get microphone access:", err);
      setError(
        "Microphone access denied. Please allow microphone access to use Luna AI."
      );

      return false;
    }
  }, [visualize]);

  // Speech timing constants (similar to Android app)
  const SILENCE_TIMEOUT_MS = 1500; // Wait 1.5s of silence before processing
  const MAX_LISTEN_TIME_MS = 10000; // Maximum listening time (10 seconds)
  const ECHO_GRACE_PERIOD_MS = 800; // Ignore speech in first 800ms to filter echo

  // Initialize speech recognition
  const initSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser.");

      return null;
    }

    const recognition = new SpeechRecognition();

    // Use continuous mode to keep listening until we detect silence
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript("");
      setResponseText("");
      setError(null);
      finalTranscriptRef.current = "";
      listenStartTimeRef.current = Date.now(); // Track start time for echo filtering

      // Set max listening timeout
      speechTimeoutRef.current = setTimeout(() => {
        if (recognitionRef.current && finalTranscriptRef.current) {
          recognition.stop();
        }
      }, MAX_LISTEN_TIME_MS);
    };

    recognition.onresult = (event) => {
      // Ignore results during grace period to filter out echo from Luna's voice
      const elapsed = Date.now() - listenStartTimeRef.current;

      if (elapsed < ECHO_GRACE_PERIOD_MS) {
        return;
      }

      let currentFinal = "";
      let currentInterim = "";

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];

        if (result.isFinal) {
          currentFinal += result[0].transcript;
        } else {
          currentInterim += result[0].transcript;
        }
      }

      // Accumulate final transcript
      if (currentFinal) {
        finalTranscriptRef.current = currentFinal;
      }

      // Show what user is saying
      setTranscript(currentFinal || currentInterim);

      // Reset silence timer on each result
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }

      // If we have a final result, wait for silence before sending
      if (currentFinal) {
        speechTimeoutRef.current = setTimeout(() => {
          // User stopped speaking - process the command
          if (recognitionRef.current) {
            recognition.stop();
          }
        }, SILENCE_TIMEOUT_MS);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }

      if (event.error === "no-speech") {
        setError("No speech detected. Please try again.");
      } else if (event.error === "audio-capture") {
        setError("No microphone found. Please check your microphone.");
      } else if (event.error !== "aborted") {
        setError(`Speech recognition error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }

      // Send the accumulated transcript if we have one
      if (finalTranscriptRef.current.trim()) {
        sendToLunaAIRef.current?.(finalTranscriptRef.current.trim());
        finalTranscriptRef.current = "";
      }
    };

    return recognition;
  }, []);

  // Send transcript to Luna AI API
  const sendToLunaAI = async (utterance) => {
    // Stop listening while processing to prevent picking up Luna's response
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      isRecognitionActiveRef.current = false;
    }
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
    }
    setIsListening(false);

    setIsProcessing(true);
    setResponseText("Processing...");

    try {
      const token = webPlayerStorage.getToken();
      const profile = webPlayerStorage.getProfile();

      // Build request body with all options (matching Android app)
      const requestBody = {
        utterance,
        voice: lunaVoice,
        audio_format: "mp3",
        include_audio: true,
        platform: "web",
        current_screen: currentScreen,
        profile_id: profile?.id || null,
      };

      // Add location if available (for weather queries)
      if (userLocation) {
        requestBody.latitude = userLocation.latitude;
        requestBody.longitude = userLocation.longitude;
      }

      // Call Luna AI directly (same as Android app)
      const response = await fetch(LUNA_CONFIG.API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": LUNA_CONFIG.API_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Failed to get response from Luna AI");
      }

      const data = await response.json();

      setResponseText(data.messageToUser || "I didn't quite catch that.");

      // Handle command if provided
      if (data.command && onCommand) {
        handleCommand(data.command);
      }

      // Play audio response if available
      if (data.audio_base64) {
        playAudioResponse(data.audio_base64, data.audio_format || "mp3");
      } else {
        // No audio - close after delay if command requires close, or restart listening
        if (shouldCloseAfterAudioRef.current) {
          shouldCloseAfterAudioRef.current = false;
          setTimeout(() => onClose(), 2000);
        } else {
          // Restart listening for follow-up
          setTimeout(() => {
            if (isOpen && !isProcessing) {
              startListening();
            }
          }, 1500);
        }
      }
    } catch (err) {
      console.error("Luna AI error:", err);
      setError("Failed to communicate with Luna AI. Please try again.");
      setResponseText("");
    } finally {
      setIsProcessing(false);
    }
  };

  sendToLunaAIRef.current = sendToLunaAI;

  // Play audio response
  const playAudioResponse = (base64Audio, format) => {
    try {
      // IMPORTANT: Stop listening while Luna speaks to prevent feedback loop
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        isRecognitionActiveRef.current = false;
      }
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
      finalTranscriptRef.current = "";

      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);

      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }

      const blob = new Blob([arrayBuffer], { type: `audio/${format}` });
      const audioUrl = URL.createObjectURL(blob);

      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }

      const audio = new Audio(audioUrl);

      audioPlayerRef.current = audio;

      // Create audio context for visualization during playback
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const source = audioContext.createMediaElementSource(audio);
      const audioAnalyser = audioContext.createAnalyser();

      source.connect(audioAnalyser);
      audioAnalyser.connect(audioContext.destination);

      audioAnalyserRef.current = audioAnalyser;
      freqsRef.current = new Uint8Array(audioAnalyser.frequencyBinCount);
      analyserRef.current = audioAnalyser;

      audio.onplay = () => {
        setIsPlayingAudio(true);
        setIsListening(false);
        visualize();
      };

      audio.onended = () => {
        setIsPlayingAudio(false);
        URL.revokeObjectURL(audioUrl);
        // Reconnect to microphone analyser
        if (mediaStreamRef.current && audioContextRef.current) {
          analyserRef.current = audioContextRef.current.createAnalyser();
          freqsRef.current = new Uint8Array(
            analyserRef.current.frequencyBinCount
          );
          const input = audioContextRef.current.createMediaStreamSource(
            mediaStreamRef.current
          );

          input.connect(analyserRef.current);
        }

        // Check if we should close after audio or restart listening
        if (shouldCloseAfterAudioRef.current) {
          shouldCloseAfterAudioRef.current = false;
          setTimeout(() => onClose(), 500);
        } else {
          // Auto-restart listening after Luna finishes speaking (like Android app)
          // Use longer delay (1.5s) to let any echo/reverb die down
          setTimeout(() => {
            if (isOpen && !isProcessing) {
              startListening();
            }
          }, 1500);
        }
      };

      audio.play();
    } catch (err) {
      console.error("Failed to play audio response:", err);
    }
  };

  // Track if we should close after audio finishes
  const shouldCloseAfterAudioRef = useRef(false);

  // Handle Luna AI commands
  const handleCommand = (command) => {
    if (!command || !command.type) return;

    const { type, target, params } = command;

    switch (type) {
      case "NAVIGATE":
        if (onCommand) {
          onCommand({ type: "navigate", target, params });
        }
        // Mark to close after audio finishes
        shouldCloseAfterAudioRef.current = true;
        break;
      case "PLAY":
        if (onCommand) {
          onCommand({ type: "play", target, params });
        }
        shouldCloseAfterAudioRef.current = true;
        break;
      case "SEARCH":
        if (onCommand) {
          onCommand({ type: "search", query: params?.query, params });
        }
        shouldCloseAfterAudioRef.current = true;
        break;
      case "NONE":
        // Follow-up question, stay open and listen again
        shouldCloseAfterAudioRef.current = false;
        break;
      default:
        break;
    }
  };

  // Track if recognition is currently active
  const isRecognitionActiveRef = useRef(false);

  // Start listening
  const startListening = useCallback(async () => {
    // Prevent multiple starts
    if (isRecognitionActiveRef.current || isListening) {
      return;
    }

    if (!permissionGranted) {
      const success = await initAudio();

      if (!success) return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();

      if (recognitionRef.current) {
        // Track recognition state
        const originalOnStart = recognitionRef.current.onstart;

        recognitionRef.current.onstart = () => {
          isRecognitionActiveRef.current = true;
          if (originalOnStart) originalOnStart();
        };

        const originalOnEnd = recognitionRef.current.onend;

        recognitionRef.current.onend = () => {
          isRecognitionActiveRef.current = false;
          if (originalOnEnd) originalOnEnd();
        };

        const originalOnError = recognitionRef.current.onerror;

        recognitionRef.current.onerror = (event) => {
          isRecognitionActiveRef.current = false;
          if (originalOnError) originalOnError(event);
        };
      }
    }

    if (recognitionRef.current && !isRecognitionActiveRef.current) {
      try {
        recognitionRef.current.start();
      } catch (err) {
        // Ignore "already started" errors
        if (!err.message?.includes("already started")) {
          console.error("Failed to start recognition:", err);
        }
      }
    }
  }, [permissionGranted, isListening, initAudio, initSpeechRecognition]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      isRecognitionActiveRef.current = false;
    }
    finalTranscriptRef.current = "";
  }, []);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        isRecognitionActiveRef.current = false;
      }
      // Fully stop and clear audio to prevent playback after close
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
        audioPlayerRef.current = null;
      }
      finalTranscriptRef.current = "";
      setIsListening(false);
      setTranscript("");
      setResponseText("");
      setError(null);
      setIsProcessing(false);
      setIsPlayingAudio(false);
    }
  }, [isOpen]);

  // Auto-start listening when overlay opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to allow animation to complete
      const timer = setTimeout(() => {
        startListening();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isOpen, startListening]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    },
    []
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          style={{
            background:
              "radial-gradient(farthest-side, #182158 0%, #030414 100%)",
          }}
        >
          {/* Close button */}
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={onClose}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Luna AI Logo Animation (shown when not listening) */}
          {!permissionGranted && lunaAnimation && (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="absolute"
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <Lottie
                loop
                animationData={lunaAnimation}
                className="w-48 h-48"
              />
            </motion.div>
          )}

          {/* Siri-style wave visualization */}
          <canvas
            className="absolute w-full"
            height={400}
            ref={canvasRef}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              opacity: permissionGranted ? 1 : 0,
            }}
            width={1000}
          />

          {/* Status indicator */}
          <div className="absolute bottom-1/4 flex flex-col items-center gap-4 z-10">
            {/* Microphone button */}
            <motion.button
              animate={{
                scale: isListening ? [1, 1.1, 1] : 1,
                boxShadow: isListening
                  ? "0 0 30px rgba(41, 200, 192, 0.6)"
                  : "0 0 0px rgba(41, 200, 192, 0)",
              }}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                isListening
                  ? "bg-cyan-500/30 border-2 border-cyan-400"
                  : "bg-white/10 border-2 border-white/30 hover:bg-white/20"
              }`}
              transition={{
                scale: { duration: 1, repeat: Infinity },
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={isListening ? stopListening : startListening}
            >
              {isListening ? (
                <Mic className="w-8 h-8 text-cyan-400" />
              ) : (
                <MicOff className="w-8 h-8 text-white/60" />
              )}
            </motion.button>

            {/* Transcript display */}
            {transcript && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="text-white/80 text-lg font-light max-w-md text-center"
                initial={{ opacity: 0, y: 10 }}
              >
                "{transcript}"
              </motion.div>
            )}

            {/* Response text */}
            {responseText && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="text-white text-xl font-medium max-w-lg text-center mt-4"
                initial={{ opacity: 0, y: 10 }}
              >
                {responseText}
              </motion.div>
            )}

            {/* Error message */}
            {error && (
              <motion.div
                animate={{ opacity: 1 }}
                className="text-red-400 text-sm max-w-md text-center mt-2"
                initial={{ opacity: 0 }}
              >
                {error}
              </motion.div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-cyan-400"
                initial={{ opacity: 0 }}
              >
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" />
                <div
                  className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                />
                <div
                  className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
              </motion.div>
            )}

            {/* Instructions */}
            {!isListening && !isProcessing && !responseText && (
              <motion.p
                animate={{ opacity: 1 }}
                className="text-white/40 text-sm"
                initial={{ opacity: 0 }}
              >
                {permissionGranted
                  ? "Tap the microphone to speak"
                  : "Click microphone to enable voice"}
              </motion.p>
            )}

            {/* Audio playing indicator */}
            {isPlayingAudio && (
              <motion.div
                animate={{ opacity: 1 }}
                className="text-cyan-400 text-sm flex items-center gap-2"
                initial={{ opacity: 0 }}
              >
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                Luna is speaking...
              </motion.div>
            )}
          </div>

          {/* Hint text at bottom */}
          <div className="absolute bottom-8 text-white/30 text-xs">
            Press ESC or click X to close
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LunaAIOverlay;
